import { STATE_PUBLISHED_HARDNESS } from '../data/statePublishedHardness.js';
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import StaticSeo from '../components/StaticSeo.jsx';
import JsonLd from '../components/JsonLd.jsx';
import {
  HARDNESS_BANDS,
  classifyHardness,
  mgLToGpg,
  hardnessColor,
  bandsForRange,
  ATLAS_PROVENANCE,
} from '../data/water.js';

// ─────────────────────────────────────────────────────────────────────────
// /water — The Water Atlas hub. Session 72, P3.
//
// This page is the citable half of P3. Everything about its construction
// follows from one constraint: a thing that cannot be read without running
// JavaScript cannot be cited, and a thing that cannot be cited cannot earn
// the links this domain does not have.
//
// So:
//   • All 50 rows are in the DEFAULT render. Sorting is a client-side
//     enhancement on top of a complete table, not the thing that produces
//     the table. Prerender (and anything that reads prerendered HTML) gets
//     every state, every number, on first paint.
//   • The hardness→method logic is body prose, not calculator output. The
//     five bands and what to do in each are readable with JS off.
//   • Dataset JSON-LD. This is a national reference table with a stated
//     source, vintage, and method — which is what schema.org/Dataset is for,
//     and it is the schema most likely to be picked up by an answer engine
//     looking for "water hardness by state" as structured data rather than
//     as a page.
//   • Provenance is stated on the page, not buried in a footnote. An atlas
//     that will not say where its numbers came from is a blog post with a
//     table in it.
//
// The data itself rides src/data/states/index.js (STATES_INDEX) — the same
// ~5 KB gzip summary chunk the /states index already loads. No new data was
// invented for this page; the hardness ranges have been in the repo since
// session 15 and were, until now, only visible one state at a time.
// ─────────────────────────────────────────────────────────────────────────

// s136 (DECISIONS #58): every figure on this page is the range the state's covered
// utilities PUBLISH (statePublishedHardness.js, generated from the rankings). The
// planning waterProfile range (#22) is shown only for a state with no published
// figure, labelled PLANNING, and never sorts or wins an extreme against published rows.
const withPublished = (s) => {
  const p = STATE_PUBLISHED_HARDNESS[s.slug];
  if (p && p.n) return { ...s, pub: true, lo: p.lo, hi: p.hi, mid: p.med, nPub: p.n };
  return { ...s, pub: false, lo: s.hardnessLow, hi: s.hardnessHigh, mid: Math.round((s.hardnessLow + s.hardnessHigh) / 2), nPub: 0 };
};
const pubFirst = (fn) => (a, b) => (b.pub - a.pub) || fn(a, b);
const SORTS = {
  name: { label: 'STATE', fn: (a, b) => a.name.localeCompare(b.name) },
  hardest: { label: 'HARDEST', fn: pubFirst((a, b) => b.hi - a.hi) },
  softest: { label: 'SOFTEST', fn: pubFirst((a, b) => a.lo - b.lo) },
  spread: {
    label: 'WIDEST RANGE',
    fn: pubFirst((a, b) => (b.hi - b.lo) - (a.hi - a.lo)),
  },
};

export default function WaterIndex() {
  const [dark, setDark] = useState(false);
  const [states, setStates] = useState(null);
  const [sort, setSort] = useState('name');

  useEffect(() => {
    let cancelled = false;
    import('../data/states/index.js').then((mod) => {
      if (!cancelled) setStates(mod.STATES_INDEX.map(withPublished));
    });
    return () => { cancelled = true; };
  }, []);

  const T = dark
    ? { bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A' }
    : { bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A' };

  const fontDisplay = "'Fraunces', serif";
  const fontBody = "'Inter Tight', sans-serif";
  const fontMono = "'JetBrains Mono', monospace";

  const rows = useMemo(() => {
    if (!states) return [];
    return [...states].sort(SORTS[sort].fn);
  }, [states, sort]);

  // National extremes, computed rather than asserted. If the data changes
  // these change with it, which is the only way a sentence like "the softest
  // municipal water in the country" stays true.
  const extremes = useMemo(() => {
    if (!states || !states.length) return null;
    const pubs = states.filter((s) => s.pub);
    if (!pubs.length) return null;
    const softest = [...pubs].sort((a, b) => a.lo - b.lo)[0];
    const hardest = [...pubs].sort((a, b) => b.hi - a.hi)[0];
    const widest = [...pubs].sort((a, b) => (b.hi - b.lo) - (a.hi - a.lo))[0];
    return { softest, hardest, widest };
  }, [states]);

  // Dataset JSON-LD — the citation surface.
  const datasetLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: 'US Water Hardness Atlas — hardness by state and city',
    description:
      'Water hardness (mg/L as CaCO₃) published by the utilities of 271 US cities, summarised by state: for each state the lowest and highest figure its covered utilities publish, banded to the USGS hardness classification. States whose covered utilities publish no figure carry a planning range labelled as such.',
    url: 'https://windowwashingguide.com/water',
    keywords: [
      'water hardness',
      'mg/L CaCO3',
      'grains per gallon',
      'municipal water',
      'United States',
      'window cleaning',
    ],
    license: 'https://windowwashingguide.com/editorial-standards',
    isAccessibleForFree: true,
    creator: {
      '@type': 'Organization',
      name: 'Window Washing Guide',
      url: 'https://windowwashingguide.com',
    },
    spatialCoverage: { '@type': 'Place', name: 'United States' },
    temporalCoverage: ATLAS_PROVENANCE.vintage,
    measurementTechnique: ATLAS_PROVENANCE.method,
    variableMeasured: [
      {
        '@type': 'PropertyValue',
        name: 'Water hardness',
        unitText: 'mg/L as CaCO₃',
        description:
          'Hardness published by the city utility for its service area (annual water quality report), expressed as calcium carbonate equivalent.',
      },
    ],
  });

  // FAQPage — the answers are the SAME STRINGS rendered in the band section
  // below. If they ever diverge, the page is lying to one of its two readers.
  const faqLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: HARDNESS_BANDS.map((b) => ({
      '@type': 'Question',
      name: `How do you clean windows with ${b.label.toLowerCase()} water (${
        b.max === Infinity ? `${b.min}+` : `${b.min}–${b.max}`
      } mg/L)?`,
      acceptedAnswer: { '@type': 'Answer', text: b.method },
    })),
  });

  const breadcrumbLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Guide', item: 'https://windowwashingguide.com/' },
      { '@type': 'ListItem', position: 2, name: 'Water Atlas', item: 'https://windowwashingguide.com/water' },
    ],
  });

  const mailtoHref =
    'mailto:water@windowwashingguide.com' +
    '?subject=' + encodeURIComponent('Water Atlas — reading submission') +
    '&body=' + encodeURIComponent(
      [
        'City / State:',
        'Water utility (if known):',
        'Hardness reading (mg/L or grains per gallon):',
        'TDS reading (ppm, if you have a meter):',
        'Source of the number (utility CCR / test strip / TDS meter / softener installer):',
        'Date of the reading:',
        '',
        'Anything else worth knowing:',
        '',
        '— Submitted to the Window Washing Guide Water Atlas',
      ].join('\n')
    );

  return (
    <div style={{
      minHeight: '100vh', background: T.bg, color: T.ink,
      fontFamily: fontBody, transition: 'background 0.3s, color 0.3s',
    }}>
      <StaticSeo path="/water" />
      <JsonLd payload={datasetLd} />
      <JsonLd payload={faqLd} />
      <JsonLd payload={breadcrumbLd} />

      <style>{`
        .wwg-water-row:hover { background: ${T.surface}; }
        .wwg-sort-btn { transition: color 0.15s, border-color 0.15s; }
        @media (max-width: 640px) {
          .wwg-water-gpg { display: none; }
        }
      `}</style>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '32px 24px 80px' }}>
        {/* Header */}
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          paddingBottom: 16, borderBottom: `1px solid ${T.rule}`, marginBottom: 32,
        }}>
          <Link to="/" style={{
            fontFamily: fontDisplay, fontSize: 22, letterSpacing: '-0.01em',
            color: T.ink, textDecoration: 'none',
          }}>
            Window Washing <em style={{ fontStyle: 'italic' }}>Guide</em>
          </Link>
          <button
            onClick={() => setDark(!dark)}
            style={{
              background: 'transparent', border: `1px solid ${T.rule}`,
              borderRadius: '50%', width: 32, height: 32,
              color: T.inkSoft, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* Breadcrumb */}
        <div style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.15em', color: T.inkFaint, marginBottom: 36,
        }}>
          <Link to="/" style={{ color: T.inkFaint, textDecoration: 'none' }}>GUIDE</Link>
          {' / '}WATER ATLAS
        </div>

        <span style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.18em', color: T.sienna,
        }}>
          REFERENCE DATA
        </span>

        <h1 style={{
          fontFamily: fontDisplay, fontSize: 48, fontWeight: 400,
          lineHeight: 1.05, letterSpacing: '-0.025em', margin: '12px 0 16px',
        }}>
          The Water Atlas
        </h1>

        <p style={{
          fontFamily: fontDisplay, fontSize: 20, fontWeight: 300,
          lineHeight: 1.5, color: T.inkSoft, margin: '0 0 24px',
        }}>
          Almost every complaint that starts with <em style={{ fontStyle: 'italic' }}>I cleaned it and
          it still looks bad</em> ends at the tap. This is what comes out of it, state by state and
          city by city, and what each number means for the glass.
        </p>

        <p style={{
          fontFamily: fontBody, fontSize: 15, lineHeight: 1.65,
          color: T.inkSoft, margin: '0 0 12px', maxWidth: 660,
        }}>
          Hardness is dissolved calcium and magnesium, measured as milligrams per litre of calcium
          carbonate equivalent. It is invisible in the glass and unmistakable on it: when hard water
          dries on a pane, the water leaves and the minerals stay. That is the whole mechanism. Everything
          below follows from it.
        </p>

        {extremes && (
          <p style={{
            fontFamily: fontBody, fontSize: 15, lineHeight: 1.65,
            color: T.inkSoft, margin: '0 0 32px', maxWidth: 660,
          }}>
            The national spread is wider than most people expect. {extremes.softest.name} runs as low as{' '}
            <strong style={{ color: T.ink, fontWeight: 500 }}>{extremes.softest.lo} mg/L</strong>;{' '}
            {extremes.hardest.name} reaches{' '}
            <strong style={{ color: T.ink, fontWeight: 500 }}>{extremes.hardest.hi} mg/L</strong>, going by the
            figures their own utilities publish. That is a factor of {Math.round(extremes.hardest.hi / Math.max(extremes.softest.lo, 1))},
            and it is why a technique that works perfectly in one state fails immediately in another
            without either person doing anything wrong.
          </p>
        )}

        {/* ── The five bands: hardness → method, as prose ─────────────────── */}
        <section style={{ marginBottom: 56 }} id="bands">
          <h2 style={{
            fontFamily: fontDisplay, fontSize: 30, fontWeight: 400,
            letterSpacing: '-0.02em', margin: '0 0 8px',
          }}>
            What your number means
          </h2>
          <p style={{
            fontFamily: fontBody, fontSize: 14, lineHeight: 1.6,
            color: T.inkSoft, margin: '0 0 28px', maxWidth: 620,
          }}>
            Five bands, on the USGS classification. Find your reading in the table below, then read the
            band it lands in. The advice changes at every boundary, and it changes because the chemistry
            does.
          </p>

          {HARDNESS_BANDS.map((b) => (
            <div key={b.code} style={{
              padding: '20px 0', borderTop: `1px solid ${T.rule}`,
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'baseline', gap: 16, marginBottom: 8, flexWrap: 'wrap',
              }}>
                <h3 style={{
                  fontFamily: fontDisplay, fontSize: 22, fontWeight: 400,
                  margin: 0, letterSpacing: '-0.01em',
                }}>
                  {b.label}
                </h3>
                <div style={{
                  fontFamily: fontMono, fontSize: 12, letterSpacing: '0.04em',
                  color: hardnessColor(b.min === 0 ? 30 : b.min, T),
                }}>
                  {b.max === Infinity ? `${b.min}+` : `${b.min}–${b.max}`} mg/L
                  <span style={{ color: T.inkFaint }}>
                    {' · '}
                    {b.max === Infinity
                      ? `${mgLToGpg(b.min)}+`
                      : `${mgLToGpg(b.min)}–${mgLToGpg(b.max)}`} gpg
                  </span>
                </div>
              </div>
              <p style={{
                fontFamily: fontBody, fontSize: 15, lineHeight: 1.7,
                color: T.inkSoft, margin: 0, maxWidth: 660,
              }}>
                {b.method}
              </p>
            </div>
          ))}

          <p style={{
            fontFamily: fontBody, fontSize: 14, lineHeight: 1.6,
            color: T.inkSoft, margin: '24px 0 0', maxWidth: 620,
            paddingTop: 16, borderTop: `1px solid ${T.rule}`,
          }}>
            To put a number on your own address rather than your state, the{' '}
            <Link to="/tools/hard-water-scorer" style={{ color: T.sienna }}>Hard Water Scorer</Link>{' '}
            takes a ZIP code. If the damage is already done, the{' '}
            <Link to="/tools/hard-water-stain-removal" style={{ color: T.sienna }}>stain removal triage</Link>{' '}
            sorts what will come off from what has etched in — and{' '}
            <Link to="/articles/hard-water-etching-vs-deposits" style={{ color: T.sienna }}>the long
            version of the chemistry</Link> explains why that distinction is permanent.
          </p>
        </section>

        {/* ── The table ───────────────────────────────────────────────────── */}
        <section style={{ marginBottom: 56 }} id="table">
          <h2 style={{
            fontFamily: fontDisplay, fontSize: 30, fontWeight: 400,
            letterSpacing: '-0.02em', margin: '0 0 8px',
          }}>
            Hardness by state
          </h2>
          <p style={{
            fontFamily: fontBody, fontSize: 14, lineHeight: 1.6,
            color: T.inkSoft, margin: '0 0 20px', maxWidth: 620,
          }}>
            The lowest and highest hardness the utilities we cover in each state publish in their own
            water quality reports. The range brackets those cities — it is not an average, and no single
            address sees the whole spread. A state marked PLANNING has no covered utility that publishes
            a figure, so its range is an estimate and sorts last. Open a state for
            its cities, utilities, and the reason its water is what it is. To compare cities directly,{' '}
            <Link to="/water/rankings" style={{ color: T.sienna }}>the city rankings</Link> order every city
            whose utility publishes a figure, hardest to softest, with dissolved solids and chloramine alongside.
          </p>

          {/* Sort control — enhancement. The table is complete before it. */}
          <div style={{
            display: 'flex', gap: 8, flexWrap: 'wrap',
            marginBottom: 4, paddingBottom: 12,
          }}>
            <span style={{
              fontFamily: fontMono, fontSize: 10, letterSpacing: '0.12em',
              color: T.inkFaint, alignSelf: 'center', marginRight: 4,
            }}>
              SORT
            </span>
            {Object.entries(SORTS).map(([key, s]) => (
              <button
                key={key}
                className="wwg-sort-btn"
                onClick={() => setSort(key)}
                style={{
                  background: 'transparent',
                  border: `1px solid ${sort === key ? T.sienna : T.rule}`,
                  color: sort === key ? T.sienna : T.inkFaint,
                  fontFamily: fontMono, fontSize: 10, letterSpacing: '0.1em',
                  padding: '5px 10px', borderRadius: 2, cursor: 'pointer',
                }}
                aria-pressed={sort === key}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Column heads */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr auto auto',
            gap: 12, padding: '10px 0',
            borderTop: `1px solid ${T.rule}`, borderBottom: `1px solid ${T.rule}`,
            fontFamily: fontMono, fontSize: 10, letterSpacing: '0.12em',
            color: T.inkFaint,
          }}>
            <span>STATE</span>
            <span className="wwg-water-gpg" style={{ textAlign: 'right' }}>GRAINS / GAL</span>
            <span style={{ textAlign: 'right' }}>MG/L CaCO₃</span>
          </div>

          {rows.map((s) => {
            const spanned = bandsForRange(s.lo, s.hi);
            const bandLabel = spanned.length > 1
              ? `${spanned[0].label} → ${spanned[spanned.length - 1].label}`
              : (spanned[0] ? spanned[0].label : '—');
            return (
              <Link
                key={s.slug}
                to={`/water/${s.slug}`}
                className="wwg-water-row"
                style={{
                  display: 'grid', gridTemplateColumns: '1fr auto auto',
                  gap: 12, padding: '14px 0', alignItems: 'baseline',
                  borderBottom: `1px solid ${T.rule}`,
                  textDecoration: 'none', color: T.ink,
                }}
              >
                <span>
                  <span style={{
                    fontFamily: fontDisplay, fontSize: 18, fontWeight: 400,
                    letterSpacing: '-0.01em',
                  }}>
                    {s.name}
                  </span>
                  <span style={{
                    fontFamily: fontMono, fontSize: 10, color: T.inkFaint,
                    marginLeft: 8, letterSpacing: '0.08em',
                  }}>
                    {s.abbreviation}
                  </span>
                  <span style={{
                    display: 'block', fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.1em', marginTop: 3,
                    color: s.pub ? hardnessColor(s.mid, T) : T.inkFaint,
                    textTransform: 'uppercase',
                  }}>
                    {bandLabel}
                  </span>
                </span>
                <span className="wwg-water-gpg" style={{
                  fontFamily: fontMono, fontSize: 12, color: T.inkFaint,
                  textAlign: 'right', whiteSpace: 'nowrap',
                }}>
                  {s.lo === s.hi ? mgLToGpg(s.lo) : `${mgLToGpg(s.lo)}–${mgLToGpg(s.hi)}`}
                </span>
                <span style={{
                  fontFamily: fontMono, fontSize: 13, textAlign: 'right',
                  whiteSpace: 'nowrap',
                  color: s.pub ? hardnessColor(s.mid, T) : T.inkFaint,
                }}>
                  {s.lo === s.hi ? s.lo : `${s.lo}–${s.hi}`}
                  {!s.pub && <span style={{ display: 'block', fontSize: 9, letterSpacing: '0.1em', color: T.inkFaint }}>PLANNING</span>}
                </span>
              </Link>
            );
          })}

          {!states && (
            <p style={{
              fontFamily: fontMono, fontSize: 11, color: T.inkFaint,
              padding: '20px 0', letterSpacing: '0.1em',
            }}>
              LOADING 50 STATES…
            </p>
          )}

          {states && (
            <p style={{
              fontFamily: fontMono, fontSize: 10, letterSpacing: '0.08em',
              color: T.inkFaint, marginTop: 16,
            }}>
              {states.length} STATES · 271 CITIES · {states.reduce((a, s) => a + (s.nPub || 0), 0)} PUBLISH A FIGURE · {ATLAS_PROVENANCE.vintageLabel.toUpperCase()}
            </p>
          )}
        </section>

        {/* ── Provenance ──────────────────────────────────────────────────── */}
        <section style={{
          marginBottom: 48, padding: '24px',
          background: T.surface, border: `1px solid ${T.rule}`, borderRadius: 3,
        }} id="method">
          <h2 style={{
            fontFamily: fontMono, fontSize: 11, letterSpacing: '0.2em',
            color: T.inkFaint, margin: '0 0 16px', textTransform: 'uppercase',
          }}>
            Where these numbers come from
          </h2>
          <p style={{
            fontFamily: fontBody, fontSize: 14, lineHeight: 1.65,
            color: T.inkSoft, margin: '0 0 12px',
          }}>
            <strong style={{ color: T.ink, fontWeight: 500 }}>Sources.</strong>{' '}
            {ATLAS_PROVENANCE.sources.charAt(0).toUpperCase() + ATLAS_PROVENANCE.sources.slice(1)}, {ATLAS_PROVENANCE.vintageLabel}.
          </p>
          <p style={{
            fontFamily: fontBody, fontSize: 14, lineHeight: 1.65,
            color: T.inkSoft, margin: '0 0 12px',
          }}>
            <strong style={{ color: T.ink, fontWeight: 500 }}>Method.</strong>{' '}
            {ATLAS_PROVENANCE.method}
          </p>
          <p style={{
            fontFamily: fontBody, fontSize: 14, lineHeight: 1.65,
            color: T.inkSoft, margin: '0 0 12px',
          }}>
            <strong style={{ color: T.ink, fontWeight: 500 }}>The limit of this data.</strong>{' '}
            {ATLAS_PROVENANCE.caveat}
          </p>
          <p style={{
            fontFamily: fontBody, fontSize: 14, lineHeight: 1.65,
            color: T.inkSoft, margin: 0,
          }}>
            Use it, quote it, link it. If you publish a figure from this table, we would rather you
            cite the page than copy the number silently — not for the traffic, but because a number
            without its provenance is how a wrong number outlives its correction. Our own working
            standards are on the{' '}
            <Link to="/editorial-standards" style={{ color: T.sienna }}>editorial standards</Link>{' '}
            page, and the full source list is under{' '}
            <Link to="/sources" style={{ color: T.sienna }}>sources</Link>.
          </p>
        </section>

        {/* ── Submit a reading ────────────────────────────────────────────── */}
        <section style={{ marginBottom: 56 }} id="submit">
          <h2 style={{
            fontFamily: fontDisplay, fontSize: 30, fontWeight: 400,
            letterSpacing: '-0.02em', margin: '0 0 8px',
          }}>
            Send us a reading
          </h2>
          <p style={{
            fontFamily: fontBody, fontSize: 15, lineHeight: 1.65,
            color: T.inkSoft, margin: '0 0 16px', maxWidth: 660,
          }}>
            This table is built from published municipal data, which means it is systematically blind
            to two things: private wells, and the gap between what a utility reports as a service-area
            average and what actually comes out of one tap. If you have a TDS meter, a softener
            installer's number, or a hardness figure off your own utility's report that disagrees with
            what we have — that is the interesting case, and we want it.
          </p>
          <p style={{
            fontFamily: fontBody, fontSize: 15, lineHeight: 1.65,
            color: T.inkSoft, margin: '0 0 20px', maxWidth: 660,
          }}>
            Readings that check out get folded into the next revision. Disagreements get looked at
            rather than filed — a reading that contradicts the table is worth more to us than one that
            confirms it.
          </p>
          <a
            href={mailtoHref}
            style={{
              display: 'inline-block',
              fontFamily: fontMono, fontSize: 11, letterSpacing: '0.14em',
              color: T.bg, background: T.sienna,
              padding: '12px 20px', borderRadius: 2, textDecoration: 'none',
            }}
          >
            SUBMIT A READING →
          </a>
          <p style={{
            fontFamily: fontMono, fontSize: 10, letterSpacing: '0.06em',
            color: T.inkFaint, margin: '12px 0 0',
          }}>
            OPENS YOUR MAIL CLIENT WITH THE FIELDS PREFILLED · water@windowwashingguide.com
          </p>
        </section>

        {/* Footer nav */}
        <footer style={{ marginTop: 40, paddingTop: 24, borderTop: `1px solid ${T.rule}` }}>
          <Link to="/states" style={{
            fontFamily: fontMono, fontSize: 11, letterSpacing: '0.18em',
            color: T.sienna, textDecoration: 'none',
          }}>
            ← STATE CLEANING GUIDES
          </Link>
        </footer>
      </div>
    </div>
  );
}
